import React, { useState, useEffect, Fragment } from "react";
import { Table, Input, notification, Spin, Select, Tooltip, Button } from "antd";
import { SearchOutlined, EyeOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { callGetApiToServer } from "../../Graphs/callApi";
import { checkPermission } from "../AdminUser/CheckPremission";
import { ACCESS_DENIED_MESSAGE, ACCESS_DENIED_MESSAGE_DESCRIPTION } from "../../constant/comman";

const { Option } = Select;

const NativeSelect = ({ value, onChange, children, className }) => {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className={className}
      style={{
        padding: "3px 8px",
        borderRadius: "4px",
        border: "1px solid #d9d9d9",
        backgroundColor: "#fff",
        outline: "none",
        cursor: "pointer",
        marginLeft: "8px",
        height: "28px"
      }}
    >
      {React.Children.map(children, (child) => {
        if (!child) return null;
        return <option value={child.props.value}>{child.props.children}</option>;
      })}
    </select>
  );
};

const AssignmentCourses = (props) => {
  const { authToken, loginUserData } = props;
  const [courses, setCourses] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [filterType, setFilterType] = useState("all"); // "all", "has_questions", "no_questions"

  const fetchCourses = async () => {
    setIsLoading(true);
    try {
      const header = {
        Accept: "application/json",
        "Content-Type": "application/json",
        authorization: "Bearer " + authToken,
      };

      const skip = (currentPage - 1) * pageSize;
      const endUrl = `v1/auth/admin/assignment-submissions/courses?skip=${skip}&limit=${pageSize}&search=${encodeURIComponent(searchText)}&sortBy=${sortBy}&sortOrder=${sortOrder}&filterType=${filterType}`;
      const res = await callGetApiToServer(header, "GET", endUrl);

      if (res.status === 200) {
        setCourses(res.data || []);
        setTotal(res.total || 0);
      } else {
        notification.error({
          message: "Error",
          description: res.message || "Failed to fetch courses data",
        });
      }
    } catch (error) {
      console.error("Error fetching assignment courses:", error);
      notification.error({
        message: "Error",
        description: "Something went wrong while fetching courses.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (loginUserData && !checkPermission(loginUserData, "assignment_submissions", "view")) {
      notification.error({
        message: ACCESS_DENIED_MESSAGE,
        description: ACCESS_DENIED_MESSAGE_DESCRIPTION,
      });
      props.history.replace("/dashboard");
      return;
    }
    fetchCourses();
  }, [authToken, currentPage, pageSize, sortBy, sortOrder, searchText, filterType, loginUserData]);

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);

    if (sorter.field) {
      setSortBy(sorter.field);
      setSortOrder(sorter.order === "descend" ? "desc" : "asc");
    }
  };

  const columns = [
    {
      title: "Course Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      render: (text, record) => (
        <Link to={`/assignment-submissions/courses/${record._id}/questions`} style={{ fontWeight: "600", color: "#4466f2" }}>
          {text}
        </Link>
      ),
    },
    {
      title: "Course Code",
      dataIndex: "category_string_id",
      key: "category_string_id",
      sorter: true,
      render: (text) => text || "N/A",
    },
    {
      title: "Total Open Book Questions",
      dataIndex: "open_book_questions_count",
      key: "open_book_questions_count",
      sorter: true,
      align: "center",
      render: (count) => <span className="badge badge-primary">{count}</span>,
    },
    {
      title: "Total Submissions",
      dataIndex: "submissions_count",
      key: "submissions_count",
      sorter: true,
      align: "center",
      render: (count) => <span className="badge badge-success">{count}</span>,
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Tooltip title="View Questions">
          <Button
            type="text"
            shape="circle"
            icon={<EyeOutlined style={{ color: "#5c6ac4" }} />}
            onClick={(e) => {
              e.stopPropagation();
              props.history.push(`/assignment-submissions/courses/${record._id}/questions`);
            }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Fragment>
      <Breadcrumb title="Assignment Submissions" parent="Dashboard" child="Courses" />
      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap" style={{ gap: "10px" }}>
            <h5>Available Courses</h5>
            <div className="d-flex align-items-center flex-wrap" style={{ gap: "15px" }}>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                style={{
                  padding: "5px 10px",
                  borderRadius: "4px",
                  border: "1px solid #d9d9d9",
                  backgroundColor: "#fff",
                  outline: "none",
                  cursor: "pointer",
                  height: "38px",
                  minWidth: "200px"
                }}
              >
                <option value="all">All Courses</option>
                <option value="has_questions">Has Open Book Questions</option>
                <option value="no_questions">No Open Book Questions</option>
              </select>
              <div style={{ position: "relative", width: "250px", height: "38px" }}>
                <span style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#bfbfbf",
                  display: "flex",
                  alignItems: "center"
                }}>
                  <SearchOutlined />
                </span>
                <input
                  type="text"
                  placeholder="Search course name..."
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{
                    width: "100%",
                    height: "38px",
                    padding: "0 30px 0 35px",
                    borderRadius: "4px",
                    border: "1px solid #d9d9d9",
                    outline: "none",
                    fontSize: "14px",
                    boxSizing: "border-box"
                  }}
                />
                {searchText && (
                  <span
                    onClick={() => handleSearch("")}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#bfbfbf",
                      cursor: "pointer",
                      fontSize: "16px",
                      display: "flex",
                      alignItems: "center",
                      userSelect: "none"
                    }}
                  >
                    &times;
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="card-body">
            {isLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={courses}
                rowKey="_id"
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: total,
                  showSizeChanger: true,
                  selectComponentClass: NativeSelect,
                }}
                onChange={handleTableChange}
                onRow={(record) => ({
                  onClick: () => {
                    props.history.push(`/assignment-submissions/courses/${record._id}/questions`);
                  },
                  style: { cursor: "pointer" }
                })}
                locale={{ emptyText: "No courses found matching your criteria." }}
              />
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  authToken: state.auth.authToken,
  loginUserData: state.auth.loginUserData,
});

export default connect(mapStateToProps)(withRouter(AssignmentCourses));
